import { loadScript } from '@mews-framework/utils';
import { isPciProxyInitialized } from './isPciProxyInitialized';
import normalizeCardType from './normalizeCardType';
import {
    CardNumberEventData,
    CardNumberRawEventData,
    CvvEventData,
    FieldEventData,
    PciProxyInline,
    PciProxyInlineFields,
    SecureFields,
} from './types';

export interface PciProxyService {
    init: (options: InitOptions) => void;
    load: (options?: LoadOptions) => Promise<HTMLScriptElement | void>;
    reset: () => void;
    tokenize: () => Promise<SuccessEventData>;
    registerCardChange: CardNumberEventHandlerRegistrar;
    registerCardBlur: CardNumberEventHandlerRegistrar;
    registerCardFocus: CardNumberEventHandlerRegistrar;
    registerCvvChange: CvvEventHandlerRegistrar;
    registerCvvFocus: CvvEventHandlerRegistrar;
    registerCvvBlur: CvvEventHandlerRegistrar;
    setCardStyle: SetStyle;
    setCvvStyle: SetStyle;
}

export interface InitOptions {
    merchantId: string;
    cardNumberFieldId: string;
    cardNumberAriaLabel?: string;
    cvvFieldId?: string;
    cvvAriaLabel?: string;
    styles?: object; // CSS?
    options: {
        debug?: boolean;
    };
}
interface LoadOptions {
    scriptUrl: string;
}

export type SetStyle = (cssRules: string) => void;

export type CardNumberEventHandler = (data: CardNumberEventData) => void;
export type CvvEventHandler = (data: CvvEventData) => void;
type FieldEventHandler = CardNumberEventHandler | CvvEventHandler;

export type CardNumberEventHandlerRegistrar = (handler: CardNumberEventHandler) => void;
export type CvvEventHandlerRegistrar = (handler: CvvEventHandler) => void;

enum EventName {
    CardNumberChange = 'cardNumber-keyUp',
    CardNumberFocus = 'cardNumber-focus',
    CardNumberBlur = 'cardNumber-blur',
    CvvChange = 'cvv-keyUp',
    CvvFocus = 'cvv-focus',
    CvvBlur = 'cvv-blur',
}

type FieldName = 'cardNumber' | 'cvv';
type EventType = 'keyUp' | 'focus' | 'blur';

interface ChangeEventData {
    event: {
        field: FieldName;
        type: EventType;
    };
    fields: {
        cardNumber: CardNumberRawEventData;
        cvv: CvvEventData;
    };
}

interface SuccessEventData {
    transactionId: string;
}

type EventMap = { [k in EventName]?: FieldEventHandler };

interface PciProxyServiceOptions {
    scriptUrl?: string;
}

const getInlineInstance = () => {
    const { Inline } = window;
    if (!Inline) {
        throw new Error('Inline script is not loaded');
    }

    return Inline;
};

export default ({ scriptUrl }: PciProxyServiceOptions = {}): PciProxyService => {
    let initialized = false;
    let handlers: EventMap = {};
    let que: EventMap = {};
    let styleQue: Array<() => void> = [];

    let secureFieldsInstance: SecureFields | null = null;

    const getSecureFieldsInstance = () => {
        if (!secureFieldsInstance) {
            throw new Error('Secure Fields instance is not created');
        }
        return secureFieldsInstance;
    };

    const getInstance = (): SecureFields | PciProxyInline =>
        secureFieldsInstance ? getSecureFieldsInstance() : getInlineInstance();

    const isSecureFieldsScriptInitialized = (
        window: Window
    ): window is Window & { SecureFields: new () => SecureFields } => !!window.SecureFields;

    // TODO: remove LoadOptions after Secure Fields are used in all places
    const load = async ({ scriptUrl: inlineScriptUrl }: Partial<LoadOptions> = {}) => {
        // backwards compatibility for not yet updated forms that provide inline pci scirpt url in the load()
        if (inlineScriptUrl) {
            return loadPciProxyInlineScript({ scriptUrl: inlineScriptUrl });
        }

        if (!scriptUrl) {
            throw new Error('PCI proxy script not provided');
        }

        return loadSecureFieldsScript();
    };

    const loadPciProxyInlineScript = async ({ scriptUrl: inlineScriptUrl }: LoadOptions) => {
        if (!isPciProxyInitialized()) {
            return loadScript(inlineScriptUrl);
        }

        return Promise.resolve();
    };

    const loadSecureFieldsScript = async () => {
        if (scriptUrl && !isSecureFieldsScriptInitialized(window)) {
            return loadScript(scriptUrl);
        }

        return Promise.resolve();
    };

    const init = ({
        merchantId,
        cardNumberFieldId,
        cardNumberAriaLabel,
        cvvFieldId,
        cvvAriaLabel,
        styles,
        options = {},
    }: InitOptions) => {
        if (isSecureFieldsScriptInitialized(window)) {
            secureFieldsInstance = new window.SecureFields();
        }

        const instance = getInstance();

        instance.on('ready', () => {
            initialized = true;

            handlers = { ...que };
            que = {};

            styleQue.forEach((s) => s());
            styleQue = [];
        });

        const fields: PciProxyInlineFields = {
            cardNumber: {
                placeholderElementId: cardNumberFieldId,
                inputType: 'tel',
                ariaLabel: cardNumberAriaLabel,
            },
        };
        if (cvvFieldId) {
            fields.cvv = {
                placeholderElementId: cvvFieldId,
                inputType: 'tel',
                ariaLabel: cvvAriaLabel,
            };
        }

        instance.initTokenize(merchantId, fields, { styles, ...options });

        instance.on('change', (data: ChangeEventData) => {
            const { field: eventField, type: eventType } = data.event;

            let fieldData: FieldEventData = data.fields[eventField];
            if (eventField === 'cardNumber') {
                fieldData = normalizeCardType(fieldData as CardNumberRawEventData);
            }

            const eventName = `${eventField}-${eventType}` as EventName;
            const handler = handlers[eventName];
            if (typeof handler === 'function') {
                // This does not work, TS can't narrow down union types with same call signatures
                // see https://github.com/Microsoft/TypeScript/issues/7294
                // handler(fieldData);

                // eslint-disable-next-line no-unused-expressions
                eventField === 'cardNumber'
                    ? (handler as CardNumberEventHandler)(fieldData as CardNumberEventData)
                    : (handler as CvvEventHandler)(fieldData as CvvEventData);
            }
        });
    };

    const reset = () => {
        initialized = false;
        que = {};
        handlers = {};
        styleQue = [];

        if (secureFieldsInstance) {
            secureFieldsInstance.destroy();
            secureFieldsInstance = null;
        }
    };

    const tokenize = async (): Promise<SuccessEventData> => {
        const instance = getInstance();

        instance.submit();
        return new Promise((resolve, reject) => {
            instance.on('success', (data: SuccessEventData) => {
                if (data.transactionId) {
                    return resolve(data);
                }
                return reject(data);
            });
        });
    };

    const registerHandler = (eventName: EventName) => (handler: FieldEventHandler) => {
        const store = initialized ? handlers : que;
        store[eventName] = handler;
    };

    const registerCardChange = registerHandler(EventName.CardNumberChange);
    const registerCardFocus = registerHandler(EventName.CardNumberFocus);
    const registerCardBlur = registerHandler(EventName.CardNumberBlur);

    const registerCvvChange = registerHandler(EventName.CvvChange);
    const registerCvvFocus = registerHandler(EventName.CvvFocus);
    const registerCvvBlur = registerHandler(EventName.CvvBlur);

    const setStyleExecutor = (fieldName: FieldName, cssString: string) => () =>
        getInstance().setStyle(fieldName, cssString);
    const setStyle = (fieldName: FieldName) => (cssString: string) => {
        const executor = setStyleExecutor(fieldName, cssString);
        if (!initialized) {
            styleQue.push(executor);
        } else {
            executor();
        }
    };
    const setCardStyle = setStyle('cardNumber');
    const setCvvStyle = setStyle('cvv');

    return {
        init,
        load,
        reset,
        tokenize,
        registerCardChange,
        registerCardBlur,
        registerCardFocus,
        registerCvvChange,
        registerCvvFocus,
        registerCvvBlur,
        setCardStyle,
        setCvvStyle,
    };
};
